import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Typography, Card, message, Spin, Badge } from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  MinusOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import VendorLayout from '../../components/VendorLayout';
import {
  getVendorBooth,
  getBoothSKUs,
  chargeWallet,
  chargeWalletWithCart,
  Booth,
  SKU,
  CartItem,
  CartItemWithSKU,
} from '../../services/vendorService';
import { auth } from '@festpix/services';
import { colors, shadows } from '@festpix/ui';

const { Title, Text } = Typography;

// Quick value buttons for fallback when no SKUs registered
const QUICK_VALUES = [1, 5, 10, 15, 20, 50];

export default function VendorChargeScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.currentUser;
  const qrCodeData = location.state?.qrCodeData;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [booth, setBooth] = useState<Booth | null>(null);
  const [skus, setSKUs] = useState<SKU[]>([]);
  const [cart, setCart] = useState<Map<string, CartItemWithSKU>>(new Map());
  const [manualAmount, setManualAmount] = useState(0);

  useEffect(() => {
    if (!qrCodeData) {
      message.error('QR Code nao encontrado');
      navigate('/vendor/scan');
      return;
    }
    loadBoothAndSKUs();
  }, [qrCodeData]);

  const loadBoothAndSKUs = async () => {
    try {
      if (!user) return;

      const boothData = await getVendorBooth(user.uid);
      setBooth(boothData);

      if (boothData) {
        const boothSKUs = await getBoothSKUs(boothData.id);
        setSKUs(boothSKUs);
      }
    } catch (error) {
      console.error('Error loading booth and SKUs:', error);
      message.error('Erro ao carregar produtos');
    } finally {
      setLoadingData(false);
    }
  };

  const handleAddToCart = (sku: SKU) => {
    if (sku.quantity === 0) {
      message.warning('Produto sem estoque');
      return;
    }

    const newCart = new Map(cart);
    const existing = newCart.get(sku.id);

    if (existing) {
      if (existing.quantity >= sku.quantity) {
        message.warning(`Estoque disponivel: ${sku.quantity}`);
        return;
      }
      newCart.set(sku.id, { ...existing, quantity: existing.quantity + 1 });
    } else {
      newCart.set(sku.id, { skuId: sku.id, quantity: 1, sku });
    }

    setCart(newCart);
  };

  const handleRemoveFromCart = (skuId: string) => {
    const newCart = new Map(cart);
    const existing = newCart.get(skuId);

    if (existing) {
      if (existing.quantity > 1) {
        newCart.set(skuId, { ...existing, quantity: existing.quantity - 1 });
      } else {
        newCart.delete(skuId);
      }
    }

    setCart(newCart);
  };

  const handleClearCart = () => {
    setCart(new Map());
    setManualAmount(0);
  };

  const handleAddValue = (value: number) => {
    setManualAmount((prev) => prev + value);
  };

  const handleClearAmount = () => {
    setManualAmount(0);
  };

  const getCartTotal = (): number => {
    let total = 0;
    cart.forEach((item) => {
      total += item.sku.price * item.quantity;
    });
    return total;
  };

  const getCartItems = (): CartItem[] => {
    return Array.from(cart.values()).map((item) => ({
      skuId: item.skuId,
      quantity: item.quantity,
    }));
  };

  const getCartQuantity = (skuId: string): number => {
    return cart.get(skuId)?.quantity || 0;
  };

  const handleCharge = async () => {
    const cartItems = getCartItems();
    const isSkuMode = skus.length > 0;
    const total = isSkuMode ? getCartTotal() : manualAmount;

    if (isSkuMode && (cartItems.length === 0 || total <= 0)) {
      message.error('Adicione itens ao carrinho');
      return;
    }

    if (!isSkuMode && total <= 0) {
      message.error('Informe um valor para cobrar');
      return;
    }

    if (!user) {
      message.error('Nao autenticado');
      return;
    }

    setLoading(true);
    try {
      if (isSkuMode) {
        await chargeWalletWithCart(qrCodeData, cartItems, user.uid);
      } else {
        await chargeWallet(qrCodeData, total, user.uid);
      }
      message.success(`Cobranca de R$ ${total.toFixed(2)} realizada com sucesso!`);
      navigate('/vendor');
    } catch (error: any) {
      message.error(error.message || 'Erro ao processar cobranca');
    } finally {
      setLoading(false);
    }
  };

  const isSkuMode = skus.length > 0;
  const displayTotal = isSkuMode ? getCartTotal() : manualAmount;
  const cartItemsList = Array.from(cart.values());

  if (loadingData) {
    return (
      <VendorLayout boothName={booth?.name}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <Spin size="large" />
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout boothName={booth?.name}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/vendor/scan')}
          style={{ marginBottom: 16, padding: 0 }}
        >
          Voltar
        </Button>

        {/* Amount Display */}
        <Card
          style={{
            borderRadius: 16,
            boxShadow: shadows.md,
            marginBottom: 16,
            textAlign: 'center',
          }}
        >
          <Text style={{ fontSize: 18, color: colors.textSecondary }}>Total</Text>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: 4 }}>
            <Text style={{ fontSize: 24, color: colors.text }}>R$</Text>
            <Text style={{ fontSize: 48, fontWeight: 'bold', color: colors.success }}>
              {displayTotal.toFixed(2)}
            </Text>
          </div>
        </Card>

        {isSkuMode ? (
          <>
            {/* SKU Grid */}
            <Card
              style={{
                borderRadius: 16,
                boxShadow: shadows.md,
                marginBottom: 16,
              }}
            >
              <Title level={5} style={{ marginBottom: 16 }}>Produtos</Title>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 12,
                }}
              >
                {skus.map((sku) => {
                  const cartQty = getCartQuantity(sku.id);
                  const isOutOfStock = sku.quantity === 0;

                  return (
                    <Badge
                      key={sku.id}
                      count={cartQty}
                      style={{ backgroundColor: colors.success }}
                    >
                      <Button
                        onClick={() => handleAddToCart(sku)}
                        disabled={isOutOfStock}
                        style={{
                          width: '100%',
                          height: 'auto',
                          padding: '12px 8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          background: isOutOfStock
                            ? colors.border
                            : cartQty > 0
                            ? colors.primaryDark
                            : colors.primary,
                          borderColor: cartQty > 0 ? colors.success : 'transparent',
                          borderWidth: cartQty > 0 ? 2 : 0,
                          borderRadius: 12,
                        }}
                      >
                        <Text
                          style={{
                            color: isOutOfStock ? colors.textSecondary : '#fff',
                            fontWeight: 600,
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                          }}
                        >
                          {sku.shortName}
                        </Text>
                        <Text
                          style={{
                            color: isOutOfStock ? colors.textSecondary : 'rgba(255,255,255,0.85)',
                            fontSize: 12,
                            marginTop: 4,
                          }}
                        >
                          R$ {sku.price.toFixed(2)}
                        </Text>
                      </Button>
                    </Badge>
                  );
                })}
              </div>
            </Card>

            {/* Cart Summary */}
            {cartItemsList.length > 0 && (
              <Card
                style={{
                  borderRadius: 16,
                  boxShadow: shadows.md,
                  marginBottom: 16,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <Title level={5} style={{ margin: 0 }}>Carrinho</Title>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={handleClearCart}
                    size="small"
                  >
                    Limpar
                  </Button>
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                  {cartItemsList.map((item) => (
                    <div
                      key={item.skuId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: `1px solid ${colors.border}`,
                      }}
                    >
                      <div>
                        <Text style={{ fontWeight: 500 }}>{item.sku.name}</Text>
                        <Text style={{ display: 'block', fontSize: 12, color: colors.textSecondary }}>
                          {item.quantity}x R$ {item.sku.price.toFixed(2)} = R$ {(item.quantity * item.sku.price).toFixed(2)}
                        </Text>
                      </div>
                      <Button
                        type="primary"
                        danger
                        icon={<MinusOutlined />}
                        size="small"
                        shape="circle"
                        onClick={() => handleRemoveFromCart(item.skuId)}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        ) : (
          <>
            {/* Manual Value Input */}
            <Card
              style={{
                borderRadius: 16,
                boxShadow: shadows.md,
                marginBottom: 16,
              }}
            >
              <Title level={5} style={{ marginBottom: 16 }}>Adicionar Valor</Title>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 12,
                }}
              >
                {QUICK_VALUES.map((value) => (
                  <Button
                    key={value}
                    onClick={() => handleAddValue(value)}
                    style={{
                      height: 48,
                      background: colors.primary,
                      borderColor: colors.primary,
                      borderRadius: 12,
                    }}
                    type="primary"
                  >
                    +R$ {value}
                  </Button>
                ))}
              </div>
              {manualAmount > 0 && (
                <Button
                  type="text"
                  danger
                  onClick={handleClearAmount}
                  style={{ marginTop: 12, width: '100%' }}
                >
                  Limpar Valor
                </Button>
              )}
            </Card>
          </>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            onClick={handleCharge}
            loading={loading}
            disabled={displayTotal <= 0}
            style={{
              height: 56,
              fontSize: 18,
              background: colors.success,
              borderColor: colors.success,
              borderRadius: 12,
            }}
          >
            Cobrar R$ {displayTotal.toFixed(2)}
          </Button>
          <Button
            size="large"
            danger
            onClick={() => navigate('/vendor/scan')}
            disabled={loading}
            style={{
              height: 48,
              borderRadius: 12,
            }}
          >
            Cancelar
          </Button>
        </div>
      </div>
    </VendorLayout>
  );
}
